/*
 * Copyright (C) 2023 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {TranslateService} from '@ngx-translate/core';
import {IComponentOptions, IPromise} from 'angular';
import {StateService} from '@uirouter/angularjs';
import {EinstellungRS} from '../../../../admin/service/einstellungRS.rest';
import {DvDialog} from '../../../../app/core/directive/dv-dialog/dv-dialog';
import {DownloadRS} from '../../../../app/core/service/downloadRS.rest';
import {AuthServiceRS} from '../../../../authentication/service/AuthServiceRS.rest';
import {
    isAtLeastFreigegeben,
    TSAntragStatus
} from '../../../../models/enums/TSAntragStatus';
import {TSEinstellungKey} from '../../../../admin/einstellungen/TSEinstellungKey';
import {TSWizardStepName} from '../../../../models/enums/TSWizardStepName';
import {TSWizardStepStatus} from '../../../../models/enums/TSWizardStepStatus';
import {TSDownloadFile} from '../../../../models/TSDownloadFile';
import {TSFreigabe} from '../../../../models/TSFreigabe';
import {ApplicationPropertyRsService} from '../../../../utils/application-property-rs/application-property-rs.service';
import {MomentUtil} from '../../../../utils/date/MomentUtil';
import {EbeguUtil} from '../../../../utils/EbeguUtil';
import {LogFactory} from '../../../../utils/log-factory/LogFactory';
import {TSRoleUtil} from '../../../../utils/TSRoleUtil';
import {AbstractGesuchViewController} from '../../../component/abstractGesuchView';
import {BerechnungsManager} from '../../../service/berechnungsManager';
import {GesuchModelManager} from '../../../service/gesuchModelManager';
import {WizardStepManager} from '../../../service/wizardStepManager';
import {FreigabeDialogController} from '../../dialog/FreigabeDialogController';
import {FreigabeZurueckziehenDialogController} from '../../dialog/FreigabeZurueckziehenDialogController';
import {FreigabeService} from '../../freigabe.service';
import IScope = angular.IScope;
import ITimeoutService = angular.ITimeoutService;

const RemoveDialogTemplate = require('../../../dialog/removeDialogTemplate.html');
const ZurueckziehenDialogTemplate = require('../../../dialog/zurueckziehenDialogTemplate.html');

const LOG = LogFactory.createLog('FreigabeViewComponent');

export class FreigabeViewComponentConfig implements IComponentOptions {
    public transclude = false;
    public bindings = {};
    public template = require('./freigabeView.html');
    public controller = FreigabeViewController;
    public controllerAs = 'vm';
}

export class FreigabeViewController extends AbstractGesuchViewController<any> {
    public static $inject = [
        'GesuchModelManager',
        'BerechnungsManager',
        'WizardStepManager',
        'DvDialog',
        'DownloadRS',
        '$scope',
        'ApplicationPropertyRsService',
        'AuthServiceRS',
        '$timeout',
        '$state',
        '$translate',
        'EinstellungRS',
        'FreigabeService'
    ];

    public isFreigebenClicked: boolean = false;
    public showGesuchFreigebenSimulationButton: boolean = false;
    public readonly TSRoleUtil = TSRoleUtil;
    public isVolksschule: boolean = false;
    private isFreigabequittungEinlesenRequired: boolean;

    public constructor(
        gesuchModelManager: GesuchModelManager,
        berechnungsManager: BerechnungsManager,
        wizardStepManager: WizardStepManager,
        private readonly dvDialog: DvDialog,
        private readonly downloadRS: DownloadRS,
        $scope: IScope,
        private readonly applicationPropertyRS: ApplicationPropertyRsService,
        private readonly authServiceRS: AuthServiceRS,
        $timeout: ITimeoutService,
        private readonly $state: StateService,
        private readonly $translate: TranslateService,
        private readonly einstellungService: EinstellungRS,
        private readonly freigabeService: FreigabeService
    ) {
        super(
            gesuchModelManager,
            berechnungsManager,
            wizardStepManager,
            $scope,
            TSWizardStepName.FREIGABE,
            $timeout
        );
        this.isVolksschule =
            this.gesuchModelManager.getDossier().gemeinde.besondereVolksschule;
        this.einstellungService
            .findEinstellung(
                TSEinstellungKey.FREIGABE_QUITTUNG_EINLESEN_REQUIRED,
                this.gesuchModelManager.getGemeinde().id,
                this.gesuchModelManager.getGesuchsperiode().id
            )
            .subscribe(
                einstellung => {
                    this.isFreigabequittungEinlesenRequired =
                        einstellung.value === 'true';
                },
                error => LOG.error(error)
            );
        this.initViewModel();
    }

    private initViewModel(): void {
        this.wizardStepManager.updateCurrentWizardStepStatusSafe(
            TSWizardStepName.FREIGABE,
            TSWizardStepStatus.IN_BEARBEITUNG
        );
        this.initDevModeParameter();
    }

    public gesuchEinreichen(): IPromise<void> {
        this.isFreigebenClicked = true;
        if (this.isGesuchValid()) {
            this.form.$setPristine();
            return this.dvDialog.showDialog(
                RemoveDialogTemplate,
                FreigabeDialogController,
                {
                    parentController: this
                }
            );
        }
        return undefined;
    }

    public confirmationCallback(): void {
        if (this.gesuchModelManager.isGesuch()) {
            const freigabeQuittung = this.openFreigabequittungPDF(true);
            if (
                EbeguUtil.isNotNullAndFalse(
                    this.isFreigabequittungEinlesenRequired
                )
            ) {
                freigabeQuittung.then(() => this.gesuchFreigeben());
            }
        } else {
            this.gesuchFreigeben(); // wenn keine freigabequittung noetig direkt freigeben
        }
    }

    /**
     * This method determines whether to schedule a reload based on the type of betreuung
     * The schedule is needed because of the nature of this async app.
     *
     * @param gesuchID
     * @param isTagesschule
     * @private
     */
    private scheduleReload(gesuchID: string, isTagesschule: boolean): void {
        const reloadAction = () => {
            this.gesuchModelManager.clearGesuch();
            this.$state.go(
                this.$state.current.name,
                {gesuchId: gesuchID},
                {reload: true}
            );
        };

        if (isTagesschule) {
            this.$timeout(reloadAction, 500);
        }
    }

    public gesuchFreigeben(): void {
        const gesuchID = this.gesuchModelManager.getGesuch().id;
        const isTagesschule = this.isThereAnySchulamtAngebot();
        this.gesuchModelManager
            .antragFreigeben(gesuchID, new TSFreigabe(null, null))
            .then(() => {
                this.berechnungsManager.clear();
                this.scheduleReload(gesuchID, isTagesschule);
            });
    }

    public freigabeZurueckziehen(): IPromise<void> {
        const gesuchID = this.gesuchModelManager.getGesuch().id;
        return this.dvDialog
            .showDialog(
                ZurueckziehenDialogTemplate,
                FreigabeZurueckziehenDialogController,
                {
                    parentController: this
                }
            )
            .then(() => {
                this.gesuchModelManager.antragZurueckziehen(gesuchID);
            });
    }

    private initDevModeParameter(): void {
        this.applicationPropertyRS
            .isDevMode()
            .subscribe((response: boolean) => {
                // Simulation nur fuer SuperAdmin freischalten
                const isSuperadmin = this.authServiceRS.isOneOfRoles(
                    TSRoleUtil.getSuperAdminRoles()
                );
                // Die Simulation ist nur im Dev-Mode moeglich und nur, wenn das Gesuch im Status FREIGABEQUITTUNG ist
                this.showGesuchFreigebenSimulationButton =
                    response &&
                    this.isGesuchInStatus(TSAntragStatus.FREIGABEQUITTUNG) &&
                    isSuperadmin;
            });
    }

    public isGesuchFreigegeben(): boolean {
        if (
            this.gesuchModelManager.getGesuch() &&
            this.gesuchModelManager.getGesuch().status
        ) {
            return (
                isAtLeastFreigegeben(
                    this.gesuchModelManager.getGesuch().status
                ) ||
                this.gesuchModelManager.getGesuch().status ===
                    TSAntragStatus.FREIGABEQUITTUNG
            );
        }
        return false;
    }

    public isFreigabequittungAusstehend(): boolean {
        if (
            this.gesuchModelManager.getGesuch() &&
            this.gesuchModelManager.getGesuch().status
        ) {
            return (
                this.gesuchModelManager.getGesuch().status ===
                TSAntragStatus.FREIGABEQUITTUNG
            );
        }
        return false;
    }

    public openFreigabequittungPDF(forceCreation: boolean): IPromise<void> {
        const win = this.downloadRS.prepareDownloadWindow();
        const gesuchId = this.gesuchModelManager.getGesuch().id;
        return this.downloadRS
            .getFreigabequittungAccessTokenGeneratedDokument(
                gesuchId,
                forceCreation
            )
            .then((downloadFile: TSDownloadFile) => {
                // wir laden das Gesuch neu, da die Erstellung des Dokumentes auch Aenderungen im Gesuch verursacht
                this.gesuchModelManager
                    .openGesuch(gesuchId)
                    .then(() => {
                        this.downloadRS.startDownloadGeneratedFile(
                            downloadFile.accessToken,
                            downloadFile.filename,
                            false,
                            win
                        );
                    })
                    .catch(ex => EbeguUtil.handleDownloadError(win, ex));
            });
    }

    public isThereAnySchulamtAngebot(): boolean {
        return this.gesuchModelManager.isThereAnySchulamtAngebot();
    }

    public getFreigabeDatum(): string {
        if (
            this.gesuchModelManager.getGesuch() &&
            this.gesuchModelManager.getGesuch().freigabeDatum
        ) {
            return MomentUtil.momentToLocalDateFormat(
                this.gesuchModelManager.getGesuch().freigabeDatum,
                'DD.MM.YYYY'
            );
        }
        return '';
    }

    public hasBerechenbareBetreuungen(): boolean {
        const gesuch = this.gesuchModelManager.getGesuch();
        return gesuch && gesuch.hasBerechenbareBetreuungen();
    }

    public getTextForFreigebenNotAllowed(): string {
        return this.freigabeService.getTextForFreigebenNotAllowed();
    }

    public canBeFreigegeben(): boolean {
        return this.freigabeService.canBeFreigegeben();
    }

    public isNotFreigegeben(): boolean {
        return (
            this.isGesuchInStatus(TSAntragStatus.IN_BEARBEITUNG_GS) ||
            this.isGesuchInStatus(TSAntragStatus.IN_BEARBEITUNG_SOZIALDIENST)
        );
    }

    public isThereAnyAbgewieseneBetreuung(): boolean {
        return this.gesuchModelManager.isThereAnyAbgewieseneBetreuung();
    }

    /**
     * Wir koennen auf jeden Fall sicher sein, dass alle Erstgesuche eine Freigabequittung haben.
     * Ausserdem nur die Mutationen bei denen alle JA-Angebote neu sind, werden eine Freigabequittung haben
     */
    public isThereFreigabequittung(): boolean {
        return this.gesuchModelManager.isGesuch();
    }

    public $postLink(): void {
        // eslint-disable-next-line no-magic-numbers
        this.doPostLinkActions(500);
    }

    public getButtonLabel(): string {
        if (this.isMutation()) {
            return this.$translate.instant('MUTATION_FREIGEBEN');
        }
        return this.$translate.instant('ANTRAG_EINREICHEN');
    }

    // nicht alle mandanten wollen hier eine Warnung. Wir zeigen diese nur, falls die Übersetzung nicht leer ist.
    public showFreigabeWarning(): boolean {
        return (
            this.isFreigabequittungAusstehend() &&
            this.$translate.instant('FREIGABEQUITTUNG_WARNUNG').length > 0
        );
    }
}
